import { useMemo } from 'react';

import {
  groupChannelsBySolution,
  type GroupableSolution,
  type SolutionChannelGroup,
} from '../../utils/group-channels-by-solution';
import type { PlatformChannel } from '../../utils/map-connection-to-row';
import {
  createLoadedSolutionResolver,
  type SolutionResolver,
} from '../../utils/resolve-solution-by-bot-id';

/**
 * The resolver the "Channels Using It" boxes group by — the ONE place that
 * picks `createLoadedSolutionResolver` over `createSolutionResolver`. The
 * difference is the whole point: with the solutions unknown (`undefined` /
 * `null`) it is `null`, so the channels are one `unresolved` list instead of a
 * false "No solution" box; an empty list is KNOWN and gets a resolver.
 *
 * Built once per `solutions` list. The table shares it across its rows.
 * Free of design-system imports, so it renders in jsdom
 * (`use-channel-groups.test.tsx`).
 */
export const useChannelGroupingResolver = (
  solutions: readonly GroupableSolution[] | null | undefined,
  customerName?: string | null
): SolutionResolver<GroupableSolution> | null =>
  useMemo(
    () => createLoadedSolutionResolver(solutions, customerName),
    [solutions, customerName]
  );

/** One channel list's boxes, see `useChannelGroupingResolver`. */
export const useChannelGroups = (
  channels: PlatformChannel[],
  solutions: readonly GroupableSolution[] | null | undefined,
  customerName?: string | null
): SolutionChannelGroup[] => {
  const resolveSolution = useChannelGroupingResolver(solutions, customerName);
  return useMemo(
    () => groupChannelsBySolution(channels, resolveSolution),
    [channels, resolveSolution]
  );
};
